<template>
  <v-slide-y-transition>
    <v-sheet
      v-if="needsBanner"
      class="cookie-banner"
      elevation="10"
      rounded="xl"
      color="surface"
      role="region"
      :aria-label="t('cookies.aria_label')"
    >
      <div class="cookie-banner__content">
        <div class="cookie-banner__text text-body-2">
          {{ t("cookies.banner_text") }}
        </div>

        <div class="cookie-banner__controls">
          <v-chip size="small" variant="tonal" label>
            {{ t("cookies.essential_required") }}
          </v-chip>

          <div class="toggles">
            <v-switch
              inset hide-details color="primary"
              v-model="analytics"
              :label="t('cookies.analytics')"
            />
            <v-switch
              inset hide-details color="primary"
              v-model="marketing"
              :label="t('cookies.marketing')"
            />
          </div>

          <div class="actions">
            <v-btn size="small" variant="text" @click="acceptEssential" class="text-none">
              {{ t("cookies.allow_essential_only") }}
            </v-btn>
            <v-btn size="small" color="primary" variant="tonal" @click="acceptSelected" class="text-none">
              {{ t("cookies.save_choices") }}
            </v-btn>
            <v-btn size="small" color="primary" @click="acceptAll" class="text-none">
              {{ t("cookies.accept_all") }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-sheet>
  </v-slide-y-transition>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import GlobalService from "@/services/global-service";
import { useConsentStore } from "@/stores/consent";

const t = (key: string) => GlobalService.translator().trans(key);

const store = useConsentStore();
const needsBanner = computed(() => store.needsBanner);
const analytics = ref<boolean>(!!store.categories.analytics);
const marketing = ref<boolean>(!!store.categories.marketing);

function acceptAll() { store.acceptAll(); }
function acceptSelected() { store.saveChoices({ analytics: analytics.value, marketing: marketing.value }); }
function acceptEssential() { store.allowEssentialOnly(); }
</script>

<style scoped>
/* Light, centered, and sized nicely on desktop */
.cookie-banner {
  position: fixed;
  inset-inline: 16px;
  bottom: 16px;
  padding: 16px;
  z-index: 9999;
  background: var(--v-theme-surface);
  color: var(--v-theme-on-surface);
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,.08);
}

.cookie-banner__content {
  display: grid;
  grid-template-areas:
    "text toggles actions";
  grid-template-columns:
    minmax(360px, 40%)
    minmax(360px, 1fr)
    minmax(260px, auto);
  gap: 12px 24px;
  align-items: center;
}

.cookie-banner__text { grid-area: text; }
.cookie-banner__controls { grid-area: toggles; display: flex; align-items: center; gap: 12px 16px; flex-wrap: wrap; }
.cookie-banner .toggles { display: flex; gap: 16px; align-items: center; }
.cookie-banner .actions { grid-area: actions; display: flex; gap: 8px; justify-content: flex-end; }

@media (min-width: 1280px) {
  .cookie-banner { max-width: 1100px; margin-inline: auto; }
}

@media (max-width: 1024px) and (min-width: 601px) {
  .cookie-banner__content {
    grid-template-columns: minmax(300px, 45%) minmax(300px, 1fr) auto;
    gap: 12px 16px;
  }
}


@media (max-width: 600px) {
  .cookie-banner {
    inset-inline: 8px;
    bottom: 8px;
    border-radius: 14px;
    padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
  }
  .cookie-banner__content {
    grid-template-areas:
      "text"
      "toggles"
      "actions";
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .cookie-banner .actions {
    width: 100%;
    flex-direction: column;
  }
  .cookie-banner .actions .v-btn { width: 100%; }
  .cookie-banner .toggles { justify-content: space-between; }
}

</style>
